import { useEffect, useMemo, useState } from "react";
import { fetchWallets } from "@/lib/api";
import { isMachineNamedWallet, walletDisplayName } from "@/lib/wallet-display-name";

export interface WalletNameEntry {
  id: string;
  name: string;
  address: string;
  /**
   * ALL-5830: true when the shown name came from no human alias and no human display name, i.e.
   * the wallet is a signer-phone or lab/tooling wallet (ALL-5795). Verdict comes from the shared
   * `isMachineNamedWallet` predicate over the same fields `mapApiWallet` keeps on a `Wallet`, so a
   * page that joins through this map hides exactly the rows the wallets page hides.
   */
  machineNamed: boolean;
}

/**
 * The tenant's wallets as id → readable name (ALL-5793, ALL-5795), read once from
 * `GET /v1/walletkit/wallets`. Pages that show a wallet only by id or alias (the transfer
 * projection carries `sourceWallet{id,name}` only) use it to show the same name the wallets
 * page shows. A failed read leaves the map empty; callers fall back to the projection's own
 * name, never to an invented one.
 */
export function useWalletNames(): { wallets: WalletNameEntry[]; byId: Map<string, WalletNameEntry>; loaded: boolean } {
  const [wallets, setWallets] = useState<WalletNameEntry[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchWallets();
        if (cancelled) return;
        if (!result.error) {
          setWallets(result.wallets.map((raw) => {
            const w = raw as Record<string, unknown>;
            const id = String(w.walletId ?? w.id ?? "");
            const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
            const address = str(w.address) ?? "";
            const display = walletDisplayName({
              alias: str(w.displayName) ?? str(w.accountAlias) ?? str(w.name) ?? str(w.label),
              walletId: id,
              network: str(w.network),
              chain: str(w.chain),
              address,
            });
            return {
              id,
              address,
              name: display.name,
              // The same derivation `mapApiWallet` (hooks/use-wallets.ts) puts on a `Wallet`:
              // the stored alias survives only when the shown name had to be derived from it.
              machineNamed: isMachineNamedWallet({
                name: display.name,
                storedAlias: display.derived && display.alias ? display.alias : null,
                walletId: id,
              }),
            };
          }).filter((w) => w.id));
        }
      } catch {
        // A failed read is not "no wallets": the map stays empty and callers fall back.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const byId = useMemo(() => new Map(wallets.map((w) => [w.id, w])), [wallets]);
  return { wallets, byId, loaded };
}
